import { csvRow } from "@/lib/csv";
import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Search, Eye, Package, ChevronDown, Download, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";

const PAGE_SIZE = 25;

const statusColors: Record<string, string> = {
  pending: "bg-yellow-100 text-yellow-800",
  processing: "bg-blue-100 text-blue-700",
  shipped: "bg-purple-100 text-purple-700",
  completed: "bg-green-100 text-green-700",
  delivered: "bg-green-100 text-green-700",
  cancelled: "bg-red-100 text-red-700",
  refunded: "bg-muted text-muted-foreground",
};

const allStatuses = ["pending", "processing", "shipped", "completed", "delivered", "cancelled", "refunded"];

const AdminOrders = () => {
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [page, setPage] = useState(0);
  const qc = useQueryClient();

  const { data: orders = [], isLoading } = useQuery({
    queryKey: ["admin-orders", search, filterStatus],
    queryFn: async () => {
      let q = supabase.from("orders").select("*").order("created_at", { ascending: false });
      if (search) {
        const sanitized = search.replace(/[%_\\]/g, '');
        const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(sanitized);
        q = q.or(`customer_name.ilike.%${sanitized}%,customer_email.ilike.%${sanitized}%,id.eq.${isUuid ? sanitized : "00000000-0000-0000-0000-000000000000"}`);
      }
      if (filterStatus !== "all") q = q.eq("status", filterStatus);
      const { data, error } = await q;
      if (error) throw error;
      return data;
    },
  });

  const totalPages = Math.max(1, Math.ceil(orders.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const pagedOrders = orders.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);

  const updateStatus = useMutation({
    mutationFn: async ({ id, status, customerEmail, customerName }: { id: string; status: string; customerEmail?: string; customerName?: string }) => {
      const { error } = await supabase.from("orders").update({ status, updated_at: new Date().toISOString() }).eq("id", id);
      if (error) throw error;
      if (customerEmail) {
        supabase.functions.invoke("send-order-email", {
          body: { type: "status_change", recipientEmail: customerEmail, customerName: customerName || "Customer", orderId: id, orderTotal: 0, newStatus: status },
        }).catch((err) => console.error("Status email failed:", err));
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-orders"] });
      qc.invalidateQueries({ queryKey: ["admin-recent-orders"] });
    },
  });

  const deleteOrder = useMutation({
    mutationFn: async (id: string) => {
      const { error: itemsErr } = await supabase.from("order_items").delete().eq("order_id", id);
      if (itemsErr) throw itemsErr;
      const { error } = await supabase.from("orders").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-orders"] });
      qc.invalidateQueries({ queryKey: ["admin-recent-orders"] });
    },
  });

  const exportCsv = () => {
    const csv = ["Order ID,Customer,Email,Status,Total,Date",
      ...orders.map((o) => csvRow([o.id, o.customer_name || "", o.customer_email || "", o.status, o.total, format(new Date(o.created_at), "yyyy-MM-dd HH:mm")]))
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `orders-export-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl font-bold">Orders</h1>
          <p className="text-sm text-muted-foreground font-body">{orders.length} orders</p>
        </div>
        <Button size="sm" variant="outline" onClick={exportCsv} disabled={orders.length === 0}>
          <Download className="w-4 h-4 mr-1.5" />Export CSV
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Search by name or email…" value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} className="pl-10" />
        </div>
        <div className="relative">
          <select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setPage(0); }}
            className="appearance-none bg-background border border-border rounded px-4 py-2 pr-9 text-sm font-body focus:outline-none focus:ring-2 focus:ring-primary/30">
            <option value="all">All Statuses</option>
            {allStatuses.map((s) => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
          </select>
          <ChevronDown className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground" />
        </div>
      </div>

      {isLoading ? (
        <div className="text-muted-foreground text-center py-20">Loading…</div>
      ) : orders.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground font-body">
          <Package className="w-10 h-10 mx-auto mb-3 opacity-40" />No orders found
        </div>
      ) : (
        <div className="bg-card rounded-lg border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-secondary/50">
                  <th className="text-left px-4 py-3 font-body font-medium text-muted-foreground">Order</th>
                  <th className="text-left px-4 py-3 font-body font-medium text-muted-foreground hidden md:table-cell">Customer</th>
                  <th className="text-left px-4 py-3 font-body font-medium text-muted-foreground">Status</th>
                  <th className="text-left px-4 py-3 font-body font-medium text-muted-foreground">Total</th>
                  <th className="text-left px-4 py-3 font-body font-medium text-muted-foreground hidden md:table-cell">Date</th>
                  <th className="text-left px-4 py-3 font-body font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pagedOrders.map((order) => (
                  <tr key={order.id} className="border-b border-border hover:bg-secondary/30 transition-colors">
                    <td className="px-4 py-3 font-body font-mono text-xs">#{order.id.slice(0, 8)}</td>
                    <td className="px-4 py-3 font-body hidden md:table-cell">
                      <p className="font-medium">{order.customer_name || "—"}</p>
                      <p className="text-xs text-muted-foreground">{order.customer_email || ""}</p>
                    </td>
                    <td className="px-4 py-3">
                      <div className="relative inline-block">
                        <select value={order.status}
                          onChange={(e) => updateStatus.mutate({ id: order.id, status: e.target.value, customerEmail: order.customer_email || undefined, customerName: order.customer_name || undefined })}
                          className={`appearance-none text-xs font-medium px-2.5 py-1 rounded pr-6 cursor-pointer ${statusColors[order.status] || "bg-muted text-muted-foreground"}`}>
                          {allStatuses.map((s) => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                        </select>
                        <ChevronDown className="w-3 h-3 absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                      </div>
                    </td>
                    <td className="px-4 py-3 font-body font-bold">£{Number(order.total).toFixed(2)}</td>
                    <td className="px-4 py-3 font-body text-muted-foreground text-xs hidden md:table-cell">{format(new Date(order.created_at), "dd MMM yyyy, HH:mm")}</td>
                    <td className="px-4 py-3 flex items-center gap-1">
                      <Link to={`/admin/orders/${order.id}`} className="p-1.5 rounded hover:bg-secondary transition-colors inline-block" title="View details">
                        <Eye className="w-4 h-4" />
                      </Link>
                      {order.status === "cancelled" && (
                        <button
                          onClick={() => { if (window.confirm("Delete this cancelled order permanently?")) deleteOrder.mutate(order.id); }}
                          className="p-1.5 rounded hover:bg-destructive/10 transition-colors inline-block text-destructive"
                          title="Delete order"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-border">
              <p className="text-sm text-muted-foreground font-body">Showing {safePage * PAGE_SIZE + 1}–{Math.min((safePage + 1) * PAGE_SIZE, orders.length)} of {orders.length}</p>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" disabled={safePage === 0} onClick={() => setPage(safePage - 1)}>Prev</Button>
                <Button size="sm" variant="ghost" disabled={safePage >= totalPages - 1} onClick={() => setPage(safePage + 1)}>Next</Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminOrders;
